import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from "react-tooltip";
import { createPortal } from 'react-dom';

import { ClickAwayListener, Grow, Paper, Popper, MenuItem, MenuList } from '@mui/material';

import ManageButton from '@/views/components/ManageButton/ManageButton';

import styles from '@/views/pages/Users/Users.module.scss';

const DisabledOption = ({
	tooltipText,
	label,
}) => {
	const [ randomId ] = useState(String(Math.random()));

	return (<>
		<div
			data-tooltip-id={ `menu-item-tooltip-${randomId}` } 
			data-tooltip-html={ tooltipText }
			data-tooltip-place="top"
			data-html="true"
		>
			<MenuItem
				className='table__menu-item'
				disabled
			>
				{label}
			</MenuItem>
		</div>
		{createPortal(
			<Tooltip 
				id={ `menu-item-tooltip-${randomId}` } 
				className={ styles["tooltip-container"] }
			/>, 
			document.body,
		)}</>
	);
};

const UsersItemMenu = ({
	onEditItem,
	setShowModalDeleteItem,
	setShowModalAddDepartment, 
	onDeleteItem = null,
	data,
}) => {
	const { dict } = useSelector((state) => state.app);
	const [ open, setOpen ] = useState(false);
	const anchorRef = useRef(null);

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event) => {
		if (
			anchorRef.current &&
            anchorRef.current.contains(event.target)
		) {
			return;
		}
		setOpen(false);
	};

	const handleEdit = (ev) => {
		setShowModalAddDepartment(true);
		onEditItem(data);
		handleClose(ev);
	};

	const handleDelete = (ev) => {
		setShowModalDeleteItem(true);
		onDeleteItem(data);
		handleClose(ev);
	};

	const handleListKeyDown = (event) => {
		if (event.key === 'Tab') {
			event.preventDefault();
			setOpen(false);
		} else if (event.key === 'Escape') {
			setOpen(false);
		}
	};

	// return focus to the button when we transitioned from !open -> open
	const prevOpen = useRef(open);
	useEffect(() => {
		if (prevOpen.current === true && open === false) {
			anchorRef.current.focus();
		}
		prevOpen.current = open;
	}, [ open ]);

	const {canEdit, canDelete} = data.rights;
	
	return (
		<>
			<ManageButton 
				forwardRef={ anchorRef } 
				open={ open }
				handleToggle={ handleToggle }
			/>
			<Popper
				open={ open }
				anchorEl={ anchorRef.current }
				role={ undefined }
				placement='bottom-start'
				transition
				disablePortal
				className='mui__popper'
			>

				{({ TransitionProps, placement }) => (
					<Grow
						{ ...TransitionProps }
						style={ {
							transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
						} }
					>
						<Paper>
							<ClickAwayListener onClickAway={ handleClose }>
								<MenuList
									autoFocusItem={ open }
									id='composition-menu'
									aria-labelledby='composition-button'
									onKeyDown={ handleListKeyDown }
								>
									{!canEdit && (
										<DisabledOption 
											tooltipText={ dict.users.list.editTooltip }
											label={ dict.users.list.edit }
										/>
									)}
									{canEdit && (
										<MenuItem
											className='table__menu-item'
											onClick={ handleEdit }
										>
											{dict.users.list.edit}
										</MenuItem>
									)}
									{!canDelete && 
										<DisabledOption 
											tooltipText={ dict.users.list.deleteTooltip }
											label={ dict.users.list.delete }
										/>}
									{canDelete && <MenuItem
										className='table__menu-item'
										onClick={ handleDelete }
										disabled={ !canDelete }
									>
										{dict.users.list.delete}
									</MenuItem>}
									
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};

export default React.memo(UsersItemMenu);